import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Input,
  Space,
  Button as AntButton,
  Tag,
  Modal,
  Button,
  Radio,
  AutoComplete,
  notification as antdNotification,
} from 'antd'
import { useNavigate, Link } from 'react-router-dom'
import Tooltip from '$components/Tooltip'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { SearchOutlined, FileOutlined } from '@ant-design/icons'
import { UilArchiveAlt } from '@iconscout/react-unicons'
import {
  archiveClientMaterial,
  fetchAllParts,
  fetchAutocompleteClientMaterials,
  fetchClientMaterials,
  invalidateFcByMaterial,
  updatePartsMaterial,
} from '$api/client'
import TextHighlighter from '$components/TextHighlighter'
import { dateWithTimeRenderer } from '$utils/i18n'
import { createPMDDeepLink } from '$utils/deepLinkUtils'
import { StyledTable } from './styledComponents'
import { discardAllOrderPartsByClientMaterialName } from '$api/evoAPIs'
import { useTheme } from 'styled-components'

const ClientMaterials = ({
  onMaterialSelect,
  selectedMaterialIdForMapping,
  dwhMaterialNameMap,
  highlightedMaterialId,
  onHighlightComplete,
  listActionTrigger,
  selectedDwhMaterialIds,
}) => {
  const { t, i18n } = useTranslation()

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const theme = useTheme()

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [isPriceModalVisible, setIsPriceModalVisible] = useState(false)
  const [sorter, setSorter] = useState({ field: 'name', order: 'ascend' })
  const [tableFilters, setTableFilters] = useState({})
  const [isArchiveModalVisible, setIsArchiveModalVisible] = useState(false)
  const [isOptionAModalVisible, setIsOptionAModalVisible] = useState(false)
  const [selectedMaterialToArchive, setSelectedMaterialToArchive] =
    useState(null)
  const [autocompleteOptions, setAutocompleteOptions] = useState([])
  const [archiveRadioValue, setArchiveRadioValue] = useState(1)
  const [suggestedMaterialName, setSuggestedMaterialName] = useState('')
  const [selectedReplacementMaterialName, setSelectedReplacementMaterialName] =
    useState('')
  const [affectedMaterialsNumber, setAffectedMaterialsNumber] = useState(0)
  const [navigateToFcPage, setNavigateToFcPage] = useState(false)
  const tableRef = useRef(null)

  const handlePartsCountClick = (record) => {
    if (record.parts_count > 0) {
      const deepLink = createPMDDeepLink(record.id, record.name)
      navigate(deepLink)
    }
  }

  const openPriceDoc = () => setIsPriceModalVisible(true)
  const closePriceDoc = () => setIsPriceModalVisible(false)

  const priceDocUrl =
    i18n.language === 'de'
      ? '/docs/Price_document_german.pdf'
      : '/docs/Price_document_english.pdf'

  const queryParams = {
    page: pagination.current,
    size: pagination.pageSize,
    sort_by: sorter.field
      ? `${sorter.order === 'descend' ? '-' : ''}${sorter.field}`
      : 'name',
    name: tableFilters.name,
  }

  const {
    data: clientMaterialsPage,
    isLoading,
    refetch: refetchClientMaterials,
  } = useQuery(
    ['clientMaterialsList', queryParams, listActionTrigger],
    () =>
      fetchClientMaterials({ queryKey: ['clientMaterialsList', queryParams] }),
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        setPagination((prev) => ({
          ...prev,
          total: data.total,
          current: data.page,
          pageSize: data.size,
        }))
      },
    },
  )

  useEffect(() => {
    if (listActionTrigger > 0) {
      refetchClientMaterials()
    }
  }, [listActionTrigger, refetchClientMaterials])

  useEffect(() => {
    if (highlightedMaterialId && clientMaterialsPage?.items) {
      const items = clientMaterialsPage.items || []
      const index = items.findIndex((item) => item.id === highlightedMaterialId)

      if (index !== -1) {
        const pageNumber = Math.floor(index / pagination.pageSize) + 1

        setPagination((prev) => ({
          ...prev,
          current: pageNumber,
        }))

        setTimeout(() => {
          if (tableRef.current) {
            const rowElement = tableRef.current.querySelector(
              `.ant-table-row[data-row-key="${highlightedMaterialId}"]`,
            )
            if (rowElement) {
              rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
              rowElement.classList.add('temp-highlight')
              setTimeout(() => {
                rowElement.classList.remove('temp-highlight')
                if (onHighlightComplete) onHighlightComplete()
              }, 2500)
            } else {
              if (onHighlightComplete) onHighlightComplete()
            }
          }
        }, 300)
      } else if (onHighlightComplete) {
        onHighlightComplete()
      }
    }
  }, [
    highlightedMaterialId,
    clientMaterialsPage,
    listActionTrigger,
    pagination.pageSize,
    onHighlightComplete,
  ])

  const sortedAndFilteredData = useMemo(() => {
    const items = clientMaterialsPage?.items || []

    if (!selectedDwhMaterialIds || selectedDwhMaterialIds.size === 0) {
      return items
    }

    const highlighted = []
    const others = []

    items.forEach((item) => {
      const itemMappedIds = new Set(
        (item.mapped_dwh_material_ids || []).map(String),
      )
      let isHighlighted = false
      for (const selectedId of selectedDwhMaterialIds) {
        if (itemMappedIds.has(selectedId)) {
          isHighlighted = true
          break
        }
      }
      if (isHighlighted) {
        highlighted.push(item)
      } else {
        others.push(item)
      }
    })

    return [...highlighted, ...others]
  }, [clientMaterialsPage, selectedDwhMaterialIds])

  const getRowClassName = (record) => {
    let classNames = []
    if (highlightedMaterialId && record.id === highlightedMaterialId) {
      classNames.push('temp-highlight')
    }
    if (
      selectedMaterialIdForMapping &&
      record.id === selectedMaterialIdForMapping
    ) {
      classNames.push('selected-for-mapping-row')
    }

    const itemMappedIds = new Set(
      (record.mapped_dwh_material_ids || []).map(String),
    )
    if (selectedDwhMaterialIds && selectedDwhMaterialIds.size > 0) {
      for (const selectedId of selectedDwhMaterialIds) {
        if (itemMappedIds.has(selectedId)) {
          classNames.push('highlighted-by-dwh-selection')
          break
        }
      }
    }

    return classNames.join(' ')
  }

  const handleTableChange = (newPagination, antdTableFilters, newSorter) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current || 1,
      pageSize: newPagination.pageSize || 10,
    }))
    if (newSorter && newSorter.field && newSorter.order) {
      setSorter({ field: newSorter.field, order: newSorter.order })
    } else if (newSorter && !newSorter.order) {
      setSorter({ field: 'name', order: 'ascend' })
    }
    const updatedApiFilters = {}
    if (antdTableFilters.name && antdTableFilters.name.length > 0) {
      updatedApiFilters.name = antdTableFilters.name[0]
    }
    setTableFilters(updatedApiFilters)
  }

  const getColumnSearchProps = (dataIndex, placeholderKey) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          placeholder={t(placeholderKey)}
          value={selectedKeys ? selectedKeys[0] : ''}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <AntButton
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90 }}
          >
            {t('search_button')}
          </AntButton>
          <AntButton
            onClick={() => {
              if (clearFilters) clearFilters()
              setSelectedKeys([])
              setTableFilters((prev) => ({ ...prev, [dataIndex]: undefined }))
              confirm({ closeDropdown: false })
            }}
            size="small"
            style={{ width: 90 }}
          >
            {t('reset_button')}
          </AntButton>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <Tooltip title={t('material_search_tooltip')}>
        <SearchOutlined
          style={{ color: filtered ? '#1890ff' : theme.colorPrimary }}
        />
      </Tooltip>
    ),
    filteredValue: tableFilters[dataIndex] ? [tableFilters[dataIndex]] : null,
  })

  const renderMappedDwhMaterials = (mappedIds) => {
    const idsArray = Array.isArray(mappedIds)
      ? mappedIds
      : mappedIds instanceof Set
        ? Array.from(mappedIds)
        : []

    const materialCount = idsArray.length

    if (materialCount === 0) {
      return (
        <Tooltip
          title={t(
            'no_mapped_materials',
            'No mapped materials / Keine gemappten Materialien',
          )}
        >
          <i style={{ color: '#999', cursor: 'help' }}>{t('unmapped')}</i>
        </Tooltip>
      )
    }

    const materialTags = idsArray.map((id) => {
      const stringId = String(id)
      const name = dwhMaterialNameMap?.[stringId]
      const displayName = name || `ID: ${stringId.substring(0, 8)}...`
      return (
        <Tag
          key={stringId}
          color="blue"
          style={{
            margin: '2px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block',
            verticalAlign: 'middle',
          }}
          title={name ? `${name} (${stringId})` : stringId}
        >
          {displayName}
        </Tag>
      )
    })

    const maxDisplayTags = 3
    const tooltipContent =
      t('mapped_materials_count', { count: materialCount }) ||
      `${materialCount} mapped DWH materials / ${materialCount} gemappte DWH Materialien`

    if (materialTags.length <= maxDisplayTags) {
      return (
        <Tooltip title={tooltipContent}>
          <div style={{ cursor: 'help' }}>{materialTags}</div>
        </Tooltip>
      )
    }

    const visibleTags = materialTags.slice(0, maxDisplayTags)
    const hiddenCount = materialTags.length - maxDisplayTags

    return (
      <Tooltip title={tooltipContent}>
        <div style={{ cursor: 'help' }}>
          {visibleTags}
          <Tooltip
            title={materialTags
              .slice(maxDisplayTags)
              .map((tag) => tag.props.children)
              .join(', ')}
          >
            <Tag color="default">+{hiddenCount} more</Tag>
          </Tooltip>
        </div>
      </Tooltip>
    )
  }

  const { data: partsQueryData, refetch: partsQueryRefetch } = useQuery(
    ['allparts'],
    fetchAllParts,
    {
      refetchOnWindowFocus: false,
    },
  )

  const handleOpenArchiveModal = (material) => {
    setAffectedMaterialsNumber(
      partsQueryData.items.filter(
        (part) =>
          part.client_material && part.client_material.id == material.id,
      ).length,
    )
    setSelectedMaterialToArchive(material)
    setIsArchiveModalVisible(true)
  }

  const handleArchiveModalCancel = () => {
    setIsArchiveModalVisible(false)
    setSelectedMaterialToArchive(null)
  }

  const handleOpenOptionModal = () => {
    if (affectedMaterialsNumber && archiveRadioValue == 1) {
      setIsOptionAModalVisible(true)
    } else if (archiveRadioValue == 2 && !selectedReplacementMaterialName) {
      antdNotification.error({ message: 'Select replacement model!' })
    } else {
      handleOptionARemoveClick()
    }
  }

  const handleCancelOptionBModal = () => {
    setSuggestedMaterialName('')
    setSelectedMaterialToArchive(null)
    setSelectedReplacementMaterialName('')
    setIsArchiveModalVisible(false)
    setArchiveRadioValue(1)
  }

  const handleOptionAModalCancel = () => {
    setIsOptionAModalVisible(false)
    setIsArchiveModalVisible(false)
  }

  const handleAutocompleteSearch = async (searchText) => {
    if (searchText && searchText.length >= 2) {
      try {
        const results = await fetchAutocompleteClientMaterials(searchText)
        // setAutocompleteOptions(results.map((name) => ({ value: name })))
        setAutocompleteOptions(
          results
            .filter((name) => name != selectedMaterialToArchive.name)
            .map((name) => ({ value: name })),
        )
      } catch (_error) {
        antdNotification.error({ message: t('autocomplete_failed_message') })
        setAutocompleteOptions([])
      }
    } else {
      setAutocompleteOptions([])
    }
  }

  const onAutoCompleteSelect = (value) => {
    setSelectedReplacementMaterialName(value)
  }

  const handleOptionARemove = useMutation({
    mutationFn: archiveClientMaterial,
    onSuccess: () => {
      handleOptionAModalCancel()
      queryClient.invalidateQueries(['clientMaterialKPIs'])
      refetchClientMaterials()
      setAffectedMaterialsNumber(0)
      discardOrderPartsMutation.mutate(selectedMaterialToArchive.name)
    },
    onError: (error) => {
      console.error('archiving failed', error)
      handleOptionAModalCancel()
    },
  })

  const handleReplaceMaterial = useMutation({
    mutationFn: updatePartsMaterial,
    onSuccess: () => {
      handleCancelOptionBModal()
      queryClient.invalidateQueries(['clientMaterialKPIs'])
      refetchClientMaterials()
    },
  })

  const discardOrderPartsMutation = useMutation({
    mutationFn: (cm_name) => discardAllOrderPartsByClientMaterialName(cm_name),
    onError: () => {
      console.error('Error while discarding offers')
    },
  })

  const invalidateFcMutation = useMutation({
    mutationFn: invalidateFcByMaterial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parts'] })
      queryClient.invalidateQueries({ queryKey: ['allparts'] })
      queryClient.invalidateQueries({ queryKey: ['partKpis'] })
    },
    onError: (error) => {
      console.error('Failed to invalidate FC status:', error)
      antdNotification.error({ message: t('error_invalidating_fc') })
    },
  })

  const navigateToFCPage = (data) => {
    navigate('/marketplace/orderservice', {
      state: { items: data, bookedService: { id: '63dcfb10f81b69c6d3a7c431' } },
    })
  }

  const handleOptionARemoveClick = async () => {
    await handleOptionARemove.mutateAsync(selectedMaterialToArchive.id)
    await invalidateFcMutation.mutateAsync({
      client_material_id: selectedMaterialToArchive.id,
      reason: 'client_material_archived',
    })
  }

  const handleOptionBRemoveClick = async () => {
    await handleOptionARemove.mutateAsync(selectedMaterialToArchive.id)
    await invalidateFcMutation.mutateAsync({
      client_material_id: selectedMaterialToArchive.id,
      reason: 'client_material_archived',
    })
    await handleReplaceMaterial.mutateAsync({
      old_material: selectedMaterialToArchive.name,
      new_material: selectedReplacementMaterialName,
    })
    await partsQueryRefetch()
  }

  useEffect(() => {
    if (handleReplaceMaterial.isSuccess && navigateToFcPage) {
      navigateToFCPage(handleReplaceMaterial.data)
    }
  }, [
    handleReplaceMaterial.data,
    handleReplaceMaterial.isSuccess,
    navigateToFcPage,
  ])

  const columns = [
    {
      title: t('client_material_column_name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sorter.field === 'name' ? sorter.order : false,
      width: 120,
      ...getColumnSearchProps('name', 'search_client_material_placeholder'),
      render: (text) => (
        <TextHighlighter
          text={text || ''}
          filters={tableFilters.name ? [{ value: tableFilters.name }] : []}
        />
      ),
    },
    {
      title: t('parts_count_column_name'),
      dataIndex: 'parts_count',
      key: 'parts_count',
      sorter: true,
      sortOrder: sorter.field === 'parts_count' ? sorter.order : false,
      width: 80,
      align: 'center',
      render: (value, record) => (
        <Tooltip
          title={
            value > 0
              ? t(
                  'click_to_view_parts_in_pmd',
                  'Click to view these parts in Parts Master Data',
                )
              : t(
                  'parts_using_tooltip',
                  'The number of parts that use this material.',
                )
          }
        >
          <span
            style={{
              cursor: value > 0 ? 'pointer' : 'default',
              color: value > 0 ? '#1890ff' : 'inherit',
              textDecoration: value > 0 ? 'underline' : 'none',
            }}
            onClick={() => handlePartsCountClick(record)}
          >
            {value || 0}
          </span>
        </Tooltip>
      ),
    },
    {
      title: (
        <Tooltip
          title={t(
            'mapped_dwh_material_tooltip',
            'The amount of DWH materials mapped to this client material / Die Anzahl DWH Materialien, die zu diesem Client Material verlinkt sind',
          )}
          placement="top"
        >
          <span style={{ cursor: 'help', borderBottom: '1px dotted #ccc' }}>
            {t('mapped_dwh_material_column_name')}
          </span>
        </Tooltip>
      ),
      dataIndex: 'mapped_dwh_material_ids',
      key: 'mapped_dwh_material_ids',
      sorter: true,
      sortOrder:
        sorter.field === 'mapped_dwh_material_ids' ? sorter.order : false,
      render: renderMappedDwhMaterials,
      width: 150,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: t('created_at_column'),
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      sortOrder: sorter.field === 'created_at' ? sorter.order : false,
      width: 70,
      render: (text) => dateWithTimeRenderer(t)(text),
      responsive: ['md'],
    },
    {
      key: 'action',
      title: '',
      align: 'center',
      width: 70,
      render: (_text, _record) => {
        return (
          <Tooltip title={t('archive_material_tooltip_placeholder')}>
            <AntButton
              type="text"
              icon={<UilArchiveAlt size="18" />}
              onClick={() => handleOpenArchiveModal(_text)}
            />
          </Tooltip>
        )
      },
    },
  ]

  return (
    <div ref={tableRef}>
      <StyledTable
        columns={columns}
        rowClassName={getRowClassName}
        dataSource={sortedAndFilteredData}
        loading={isLoading}
        rowKey="id"
        tableLayout="fixed"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: clientMaterialsPage?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        onRow={(record) => {
          return {
            onClick: () => {
              if (onMaterialSelect) {
                onMaterialSelect(record.id, record)
              }
            },
          }
        }}
      />
      <Modal
        title={
          affectedMaterialsNumber
            ? t('my_materials.affected_no_of_parts', {
                number: affectedMaterialsNumber,
              })
            : 'Are you sure you want to archive this material?'
        }
        open={isArchiveModalVisible}
        onOk={handleOpenOptionModal}
        okText={t('button.remove')}
        onCancel={handleArchiveModalCancel}
        cancelText={t('button.cancel')}
      >
        {affectedMaterialsNumber ? (
          <Radio.Group
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            key="archiveRadioValue"
            onChange={(e) => setArchiveRadioValue(e.target.value)}
            value={archiveRadioValue}
            options={[
              {
                value: 1,
                label: t('my_materials.client_material_archive_option_a'),
              },
              {
                value: 2,
                label: t('my_materials.client_material_archive_option_b'),
              },
            ]}
          />
        ) : (
          ''
        )}
        {archiveRadioValue === 2 && (
          <AutoComplete
            style={{ width: '100%', marginBlock: '10px' }}
            placeholder={t('enter_material_name_placeholder')}
            options={autocompleteOptions}
            value={suggestedMaterialName}
            onChange={(value) => setSuggestedMaterialName(value)}
            onSearch={handleAutocompleteSearch}
            onSelect={onAutoCompleteSelect}
            filterOption={false}
            showSearch={true}
            allowClear={true}
          />
        )}
      </Modal>
      <Modal
        style={{ paddingInline: '5px' }}
        title={t('my_materials.all_fc_not_valid_perform_again')}
        open={isOptionAModalVisible}
        okText={t('button.remove')}
        cancelText={t('button.cancel')}
        onOk={handleOptionARemoveClick}
        onCancel={handleOptionAModalCancel}
      />
      <Modal
        width={800}
        style={{ paddingInline: '10px' }}
        open={selectedReplacementMaterialName}
        onCancel={handleCancelOptionBModal}
        title={
          <p>
            {t('my_materials.by_changing_the_client_material')}
            <span>
              &nbsp;
              <Link style={{ fontWeight: '500' }} onClick={openPriceDoc}>
                {t('Price documentation')}
                <FileOutlined />
              </Link>
            </span>
          </p>
        }
        footer={[
          <div
            key="othermaterial"
            style={{ display: 'flex', flexDirection: 'column', padding: '6px' }}
          >
            <Button
              type="primary"
              onClick={() => {
                handleOptionBRemoveClick()
                setNavigateToFcPage(true)
              }}
            >
              {t('my_materials.fc_after_client_material_archive')}
            </Button>
            <Button
              style={{ marginTop: '5px' }}
              onClick={handleOptionBRemoveClick}
            >
              {t('my_materials.no_fc_after_client_material_archive')}
            </Button>
            <Button
              style={{ marginTop: '5px' }}
              onClick={handleCancelOptionBModal}
            >
              {t('button.cancel')}
            </Button>
          </div>,
        ]}
      ></Modal>
      <Modal
        title={t('Price documentation')}
        open={isPriceModalVisible}
        onCancel={closePriceDoc}
        footer={null}
        width="80%"
        style={{ top: 20 }}
      >
        <embed
          src={priceDocUrl}
          type="application/pdf"
          width="100%"
          height="600px"
        />
      </Modal>
    </div>
  )
}

export default ClientMaterials
